import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CreatedServiceKey } from "../../../packages/contracts/src/production";
import { runDate } from "./MethodNavigation";
import { Button } from "./components/ui/button";
import { productionApi } from "./production-api";

/** API keys let an app start runs of a published Method. `method connect` makes one for an app. */
export function ApiKeys() {
  const client = useQueryClient();
  const keys = useQuery({ queryKey: ["service-keys"], queryFn: productionApi.keys });
  const [name, setName] = useState("");
  const [created, setCreated] = useState<CreatedServiceKey | null>(null);
  const create = useMutation({ mutationFn: productionApi.createKey, onSuccess: key => { setCreated(key); setName(""); void client.invalidateQueries({ queryKey: ["service-keys"] }); } });
  const revoke = useMutation({ mutationFn: productionApi.revokeKey, onSuccess: () => client.invalidateQueries({ queryKey: ["service-keys"] }) });
  return <section className="api-keys" aria-label="API keys">
    <h2>API keys</h2>
    <p className="list-muted">An app uses a key to run your published Methods. The easiest way to make one is "Use in your app" on a Method page.</p>
    <form className="list-controls" onSubmit={event => { event.preventDefault(); if (name.trim()) create.mutate(name.trim()); }}>
      <input aria-label="Key name" placeholder="Key name, for example Backend" value={name} maxLength={100} onChange={event => setName(event.target.value)} />
      <Button type="submit" disabled={!name.trim() || create.isPending}>Create key</Button>
    </form>
    {create.error && <p role="alert">{create.error.message}</p>}
    {created && <div className="dash-state" role="status">
      <p>Copy these values now. They are shown only once. Save them in your app's secret store as METHOD_API_KEY and METHOD_WEBHOOK_SECRET.</p>
      <code className="break-all">{created.key}</code>
      {created.webhook_secret && <code className="break-all">{created.webhook_secret}</code>}
      <Button variant="secondary" onClick={() => setCreated(null)}>I saved them</Button>
    </div>}
    {keys.error && <p role="alert">{keys.error.message}</p>}
    {keys.data?.length ? <div className="workspace-table-scroll"><table className="workspace-table">
      <thead><tr><th>Name</th><th>Key</th><th>Last used</th><th /></tr></thead>
      <tbody>{keys.data.map(key => <tr key={key.id}>
        <td>{key.name}</td><td><code>{key.prefix}…</code></td>
        <td>{key.revoked_at ? "Revoked" : key.last_used_at ? runDate(key.last_used_at) : "Never"}</td>
        <td>{!key.revoked_at && <Button variant="secondary" disabled={revoke.isPending} onClick={() => { if (window.confirm(`Revoke ${key.name}? Apps and workers that use it stop at once.`)) revoke.mutate(key.id); }}>Revoke</Button>}</td>
      </tr>)}</tbody>
    </table></div> : !keys.isPending && <p className="list-muted">No API keys yet.</p>}
  </section>;
}
